// src/pages/AccountPage.js
import { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, storage, db } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import { useUserDoc } from '../hooks/useUserDoc';
import { startCheckout } from '../utils/stripe';
import Navbar from '../components/Navbar';
import CircleIcon, { SvgIcon, ZelleLogo, VenmoLogo, StripeLogo } from '../components/Icon';
import Lightbox from '../components/Lightbox';
import { FULL_ACCESS_MODE } from '../config/flags';

export default function AccountPage() {
  const { user, refreshUser, logout } = useAuth();
  const { userDoc, isPremium, isBusiness, reload } = useUserDoc();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [nameMsg, setNameMsg] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  const [connectLoading, setConnectLoading] = useState(false);
  const [connectMsg, setConnectMsg] = useState('');

  const [pwMsg, setPwMsg] = useState('');
  const [pwSending, setPwSending] = useState(false);

  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoMsg, setPhotoMsg] = useState('');
  const fileRef = useRef();

  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const [zelleId, setZelleId] = useState('');
  const [venmoUsername, setVenmoUsername] = useState('');
  const [paypalUsername, setPaypalUsername] = useState('');
  const [mercadoPagoUsername, setMercadoPagoUsername] = useState('');
  const [paymentMsg, setPaymentMsg] = useState('');
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Sync form fields when userDoc loads (fixes mobile where userDoc is null on first render)
  useEffect(() => {
    if (userDoc) {
      setZelleId(userDoc.zelleId || '');
      setVenmoUsername(userDoc.venmoUsername || '');
      setPaypalUsername(userDoc.paypalUsername || '');
      setMercadoPagoUsername(userDoc.mercadoPagoUsername || '');
    }
  }, [userDoc]);

  // Handle Stripe Connect return URL
  useEffect(() => {
    const connectStatus = searchParams.get('connect');
    if (connectStatus === 'success') {
      setConnectMsg(t('account.connectSuccess'));
      if (reload) reload();
      searchParams.delete('connect');
      setSearchParams(searchParams, { replace: true });
      setTimeout(() => setConnectMsg(''), 6000);
    } else if (connectStatus === 'retry') {
      setConnectMsg(t('account.connectRetry'));
      searchParams.delete('connect');
      setSearchParams(searchParams, { replace: true });
      setTimeout(() => setConnectMsg(''), 6000);
    }
  }, [searchParams, setSearchParams, reload, t]);

  const isGoogleUser = user?.providerData?.[0]?.providerId === 'google.com';
  const tierLabel = FULL_ACCESS_MODE ? '' : isBusiness ? 'Business' : isPremium ? 'Premium' : 'Free';
  const tierClass = FULL_ACCESS_MODE ? '' : isBusiness ? 'badge-business' : isPremium ? 'badge-premium' : 'badge-pending';

  const handleNameUpdate = async () => {
    if (!displayName.trim()) return;
    setNameSaving(true); setNameMsg('');
    try {
      await updateProfile(auth.currentUser, { displayName: displayName.trim() });
      await setDoc(doc(db, 'users', user.uid), { displayName: displayName.trim() }, { merge: true });
      await refreshUser();
      setNameMsg(t('account.nameUpdated'));
    } catch { setNameMsg(t('account.nameFailed')); }
    finally { setNameSaving(false); setTimeout(() => setNameMsg(''), 3000); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setPhotoMsg(t('account.selectImage')); return; }
    if (file.size > 2 * 1024 * 1024) { setPhotoMsg(t('account.maxSize')); return; }
    setPhotoUploading(true); setPhotoMsg('');
    try {
      const storageRef = ref(storage, `profilePhotos/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateProfile(auth.currentUser, { photoURL: url });
      await setDoc(doc(db, 'users', user.uid), { photoURL: url }, { merge: true });
      await refreshUser();
      setPhotoMsg(t('account.photoUpdated'));
    } catch (err) {
      console.error('Photo upload error:', err);
      setPhotoMsg(t('account.uploadFailed'));
    } finally { setPhotoUploading(false); setTimeout(() => setPhotoMsg(''), 3000); }
  };

  const handlePasswordReset = async () => {
    setPwSending(true); setPwMsg('');
    try {
      await sendPasswordResetEmail(auth, user.email);
      setPwMsg(t('account.resetSent'));
    } catch { setPwMsg(t('account.resetFailed')); }
    finally { setPwSending(false); setTimeout(() => setPwMsg(''), 5000); }
  };

  const handlePaymentInfoSave = async () => {
    setPaymentSaving(true); setPaymentMsg('');
    try {
      // merge: true ensures we only update the fields we're changing
      const update = {};
      if (zelleId.trim() !== (userDoc?.zelleId || '')) {
        update.zelleId = zelleId.trim();
      }
      if (venmoUsername.trim().replace(/^@/, '') !== (userDoc?.venmoUsername || '')) {
        update.venmoUsername = venmoUsername.trim().replace(/^@/, '');
      }
      if (paypalUsername.trim() !== (userDoc?.paypalUsername || '')) {
        update.paypalUsername = paypalUsername.trim();
      }
      if (mercadoPagoUsername.trim() !== (userDoc?.mercadoPagoUsername || '')) {
        update.mercadoPagoUsername = mercadoPagoUsername.trim();
      }
      if (Object.keys(update).length === 0) {
        setPaymentMsg(t('account.noChanges'));
        setPaymentSaving(false);
        setTimeout(() => setPaymentMsg(''), 2000);
        return;
      }
      await setDoc(doc(db, 'users', user.uid), update, { merge: true });
      if (reload) reload();
      setPaymentMsg(t('account.paymentSaved'));
    } catch { setPaymentMsg(t('account.paymentFailed')); }
    finally { setPaymentSaving(false); setTimeout(() => setPaymentMsg(''), 3000); }
  };

  const handleConnectStripe = async () => {
    setConnectLoading(true); setConnectMsg('');
    try {
      const functions = getFunctions();
      const createLink = httpsCallable(functions, 'createConnectOnboardingLink');
      const result = await createLink({ origin: window.location.origin });
      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      console.error('Connect error:', err);
      setConnectMsg('Failed to start Stripe onboarding. Try again.');
      setConnectLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const functions = getFunctions();
      const createPortal = httpsCallable(functions, 'createCustomerPortalSession');
      const result = await createPortal({ origin: window.location.origin });
      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      console.error('Portal error:', err);
      setPortalLoading(false);
    }
  };

  const handleCheckout = async (tier) => {
    setCheckoutLoading(tier);
    try {
      await startCheckout(tier);
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutLoading(null);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      const fns = getFunctions();
      const deleteAccount = httpsCallable(fns, 'deleteAccount');
      await deleteAccount();
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Delete account error:', err);
      setDeleteError(t('account.deleteFailed'));
      setDeleting(false);
    }
  };

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
        <h1 style={{ fontSize: '1.4rem', marginBottom: 20 }}>{t('account.title')}</h1>

        {/* Profile photo */}
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            onClick={() => user?.photoURL ? setLightboxOpen(true) : fileRef.current?.click()}
            style={{
              width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
              background: user?.photoURL ? `url(${user.photoURL}) center/cover` : 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: '1.5rem', fontWeight: 700, cursor: 'pointer',
              border: '2px solid var(--border)',
            }}
          >
            {!user?.photoURL && (user?.displayName?.[0]?.toUpperCase() || '?')}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '1rem' }}>
              {user?.displayName || t('account.noName')}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{user?.email}</div>
            {!FULL_ACCESS_MODE && (
              <div style={{ marginTop: 4 }}>
                <span className={`badge ${tierClass}`}>{tierLabel}</span>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
          <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={photoUploading} style={{ marginBottom: 0 }}>
            {photoUploading ? '...' : t('account.editPhoto')}
          </button>
        </div>
        {photoMsg && <p style={{ fontSize: '0.78rem', color: photoMsg === t('account.photoUpdated') ? 'var(--confirmed)' : 'var(--declined)', marginTop: 4 }}>{photoMsg}</p>}

        {/* Display name */}
        <div className="section-header">
          <div className="section-title">{t('account.displayName')}</div>
        </div>
        <div className="card">
          <div className="form-group" style={{ marginBottom: 8 }}>
            <input className="form-input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder={t('account.yourName')} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleNameUpdate} disabled={nameSaving} style={{ marginBottom: 0 }}>
            {nameSaving ? t('common.saving') : t('account.updateName')}
          </button>
          {nameMsg && <p style={{ fontSize: '0.78rem', color: nameMsg.includes('fail') ? 'var(--declined)' : 'var(--confirmed)', marginTop: 6 }}>{nameMsg}</p>}
        </div>

        {/* Payment info — Premium/Business */}
        {(FULL_ACCESS_MODE || isPremium) && (
          <>
            <div className="section-header">
              <div className="section-title">{t('account.paymentInfo')}</div>
              <div className="section-subtitle">{t('account.paymentInfoSub')}</div>
            </div>
            <div className="card">
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><ZelleLogo size={16}/> {t('account.zelleLabel')}</label>
                <input className="form-input" value={zelleId} onChange={e => setZelleId(e.target.value)} placeholder={t('account.zellePlaceholder')} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><VenmoLogo size={16}/> {t('account.venmoLabel')}</label>
                <input className="form-input" value={venmoUsername} onChange={e => setVenmoUsername(e.target.value)} placeholder={t('account.venmoPlaceholder')} />
              </div>
              <div className="form-group">
                <label className="form-label">PayPal</label>
                <input className="form-input" value={paypalUsername} onChange={e => setPaypalUsername(e.target.value)} placeholder="paypal.me/username" />
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label className="form-label">Mercado Pago</label>
                <input className="form-input" value={mercadoPagoUsername} onChange={e => setMercadoPagoUsername(e.target.value)} placeholder="mpago.la/username" />
              </div>
              <button className="btn btn-primary btn-sm" onClick={handlePaymentInfoSave} disabled={paymentSaving} style={{ marginBottom: 0 }}>
                {paymentSaving ? t('common.saving') : t('account.savePaymentInfo')}
              </button>
              {paymentMsg && <p style={{ fontSize: '0.78rem', color: paymentMsg.includes('fail') || paymentMsg.includes('No changes') ? 'var(--muted)' : 'var(--confirmed)', marginTop: 6 }}>{paymentMsg}</p>}
            </div>
          </>
        )}

        {/* Stripe Connect */}
        {(FULL_ACCESS_MODE || isPremium) && (
          <>
            <div className="section-header">
              <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><StripeLogo size={20}/> {t('account.stripeConnect')}</div>
              <div className="section-subtitle">{t('account.stripeConnectSub')}</div>
            </div>
            <div className="card">
              {userDoc?.stripeConnectAccountId ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ color: 'var(--confirmed)', fontSize: '1.1rem' }}>✓</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--confirmed)' }}>{t('account.stripeConnected')}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>Account: {userDoc.stripeConnectAccountId}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginBottom: 10 }}>
                    {t('account.stripeFeeLine')}
                  </p>
                  <button className="btn btn-danger btn-sm" style={{ marginBottom: 0 }} onClick={async () => {
                    if (!window.confirm(t('account.disconnectConfirm'))) return;
                    try {
                      await setDoc(doc(db, 'users', user.uid), { stripeConnectAccountId: null }, { merge: true });
                      setConnectMsg(t('account.disconnected'));
                      if (reload) reload();
                      setTimeout(() => setConnectMsg(''), 4000);
                    } catch { setConnectMsg(t('account.disconnectFailed')); }
                  }}>
                    {t('account.disconnectStripe')}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 10 }}>
                    {t('account.connectPrompt')}
                  </p>
                  <button className="btn btn-primary btn-sm" onClick={handleConnectStripe} disabled={connectLoading} style={{ marginBottom: 0 }}>
                    {connectLoading ? t('account.redirectingStripe') : t('account.connectStripe')}
                  </button>
                </>
              )}
              {connectMsg && <p style={{ fontSize: '0.78rem', color: connectMsg.includes('fail') || connectMsg.includes('incomplete') ? 'var(--declined)' : 'var(--confirmed)', marginTop: 8 }}>{connectMsg}</p>}
            </div>
          </>
        )}

        {/* Subscription */}
        {!FULL_ACCESS_MODE && (
          <>
            <div className="section-header">
              <div className="section-title">{t('account.subscription')}</div>
              <div className="section-subtitle">{t('account.currentPlan')} <strong>{tierLabel}</strong></div>
            </div>
            <div className="card">
              {!isPremium && (
                <>
                  <button className="btn btn-premium" onClick={() => handleCheckout('premium')} disabled={checkoutLoading === 'premium'} style={{ marginBottom: 8 }}>
                    {checkoutLoading === 'premium' ? t('account.redirectingStripe') : t('account.upgradePremium')}
                  </button>
                  <button className="btn btn-business" onClick={() => handleCheckout('business')} disabled={checkoutLoading === 'business'} style={{ marginBottom: 0 }}>
                    {checkoutLoading === 'business' ? t('account.redirectingStripe') : t('account.upgradeBusiness')}
                  </button>
                </>
              )}
              {isPremium && !isBusiness && (
                <button className="btn btn-business" onClick={() => handleCheckout('business')} disabled={checkoutLoading === 'business'} style={{ marginBottom: 8 }}>
                  {checkoutLoading === 'business' ? t('account.redirectingStripe') : t('account.upgradeBusiness')}
                </button>
              )}
              {isBusiness && (
                <p style={{ fontSize: '0.84rem', color: 'var(--confirmed)', fontWeight: 600, marginBottom: 8 }}>{t('account.onBusinessPlan')}</p>
              )}
              {isPremium && userDoc?.stripeCustomerId && (
                <button className="btn btn-secondary" onClick={handleManageSubscription} disabled={portalLoading} style={{ marginBottom: 0 }}>
                  {portalLoading ? t('account.redirecting') : t('account.manageSubscription')}
                </button>
              )}
            </div>
          </>
        )}

        {/* Change Password — below Subscription */}
        <div className="section-header">
          <div className="section-title">{t('account.password')}</div>
        </div>
        <div className="card">
          {isGoogleUser ? (
            <p style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>
              {t('account.googlePassword')}{' '}
              <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {t('account.googleSettings')}
              </a>.
            </p>
          ) : (
            <>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 10 }}>
                {t('account.resetPrompt')} <strong>{user?.email}</strong>.
              </p>
              <button className="btn btn-primary btn-sm" onClick={handlePasswordReset} disabled={pwSending} style={{ marginBottom: 0 }}>
                {pwSending ? t('common.saving') : t('account.sendReset')}
              </button>
              {pwMsg && <p style={{ fontSize: '0.78rem', color: pwMsg.includes('fail') ? 'var(--declined)' : 'var(--confirmed)', marginTop: 6 }}>{pwMsg}</p>}
            </>
          )}
        </div>

        {!FULL_ACCESS_MODE && !isPremium && (
          <div className="upsell-banner" style={{ marginTop: 16 }}>
            <div className="upsell-banner-title"><SvgIcon name="heart" size={14} color="var(--premium)" /> {t('account.unlockAll')}</div>
            <Link to="/pricing" className="btn btn-premium" style={{ padding: '9px', fontSize: '0.82rem', marginBottom: 0 }}>
              {t('account.seePricingPlans')}
            </Link>
          </div>
        )}

        {/* Danger Zone */}
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
          <div className="section-header" style={{ margin: '0 0 12px' }}>
            <div className="section-title" style={{ color: 'var(--declined)' }}>{t('account.dangerZone')}</div>
          </div>
          <button
            className="btn"
            style={{
              background: 'var(--surface)', color: 'var(--declined)',
              border: '1.5px solid var(--declined)', marginBottom: 0,
            }}
            onClick={() => { setDeleteModalOpen(true); setDeleteConfirmed(false); setDeleteError(''); }}
          >
            {t('account.deleteAccount')}
          </button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {deleteModalOpen && (
        <div
          onClick={() => !deleting && setDeleteModalOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 'var(--radius)',
              padding: 24, maxWidth: 400, width: '100%',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <h2 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: 10, color: 'var(--declined)' }}>
              {t('account.deleteTitle')}
            </h2>
            <p style={{ fontSize: '0.84rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
              {t('account.deleteBody')}
            </p>
            <div className="check-row" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={deleteConfirmed}
                onChange={(e) => setDeleteConfirmed(e.target.checked)}
                id="delete-confirm-check"
              />
              <label htmlFor="delete-confirm-check" className="check-label" style={{ fontSize: '0.82rem' }}>
                {t('account.deleteConfirmLabel')}
              </label>
            </div>
            {deleteError && (
              <p style={{ fontSize: '0.78rem', color: 'var(--declined)', marginBottom: 10 }}>{deleteError}</p>
            )}
            <button
              className="btn"
              style={{ background: 'var(--declined)', color: 'white', border: 'none', marginBottom: 8 }}
              onClick={handleDeleteAccount}
              disabled={!deleteConfirmed || deleting}
            >
              {deleting ? t('account.deleting') : t('account.deleteConfirm')}
            </button>
            <button
              className="btn btn-secondary"
              style={{ marginBottom: 0 }}
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleting}
            >
              {t('account.deleteCancel')}
            </button>
          </div>
        </div>
      )}

      {lightboxOpen && user?.photoURL && (
        <Lightbox src={user.photoURL} alt={user.displayName} onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
}
